import React from 'react';
import PropTypes from 'prop-types';
import { useIntl } from '@edx/frontend-platform/i18n';
import { Form } from '@openedx/paragon';

import SectionSubHeader from '../../generic/section-sub-header';
import { ScheduleRow, SCHEDULE_ROW_TYPES } from './schedule-row';
import { CertificateDisplayRow } from './certificate-display-row';
import messages from './messages';

const SCHEDULE_FLAGS = [
  { key: 'courseLive', label: 'Course is Live', description: 'Does not affect course start dates / visibility' },
  { key: 'comingSoon', label: 'Coming Soon', description: '' },
  { key: 'isSkillModule', label: 'Is Skill Module', description: '' },
  { key: 'isTrack', label: 'Is Track', description: '' },
  { key: 'isPathway', label: 'Is Pathway', description: '' },
  { key: 'skillBadging', label: 'Skill Badging', description: '' },
  { key: 'hideFromCatalog', label: 'Hide from Catalog', description: '' },
];

const ScheduleSection = ({
  endDate,
  startDate,
  errorFields,
  platformName,
  enrollmentEnd,
  enrollmentStart,
  upgradeDeadline,
  enrollmentEndEditable,
  certificateAvailableDate,
  certificatesDisplayBehavior,
  canShowCertificateAvailableDateField,
  courseLive,
  comingSoon,
  isSkillModule,
  isTrack,
  isPathway,
  skillBadging,
  hideFromCatalog,
  onChange,
}) => {
  const intl = useIntl();
  const flagValues = {
    courseLive, comingSoon, isSkillModule, isTrack, isPathway, skillBadging, hideFromCatalog,
  };

  const enrollmentEndHelpText = intl.formatMessage(
    messages.scheduleEnrollmentEndDateHelpText,
  );
  const enrollmentEndHelpTexRestricted = intl.formatMessage(
    messages.scheduleEnrollmentEndDateRestrictedHelpText,
    { platformName },
  );
  const computedEnrollmentEndHelpText = `${enrollmentEndHelpText} ${
    !enrollmentEndEditable ? enrollmentEndHelpTexRestricted : ''
  }`;

  const courseDateFields = [
    {
      value: startDate,
      labels: [
        intl.formatMessage(messages.scheduleCourseStartDateLabel),
        intl.formatMessage(messages.scheduleCourseStartTimeLabel),
      ],
      rowType: SCHEDULE_ROW_TYPES.datetime,
      helpText: intl.formatMessage(messages.scheduleCourseStartDateHelpText),
      controlName: 'startDate',
      errorFeedback: errorFields?.startDate,
    },
    {
      labels: [
        intl.formatMessage(messages.scheduleCourseEndDateLabel),
        intl.formatMessage(messages.scheduleCourseEndTimeLabel),
      ],
      value: endDate,
      rowType: SCHEDULE_ROW_TYPES.datetime,
      helpText: intl.formatMessage(messages.scheduleCourseEndDateHelpText),
      controlName: 'endDate',
      errorFeedback: errorFields?.endDate,
    },
    {
      skip: !canShowCertificateAvailableDateField,
      key: 'certificate-display-row',
      rowType: SCHEDULE_ROW_TYPES.dropdown,
      certificateAvailableDate,
      availableDateErrorFeedback: errorFields?.certificateAvailableDate,
      certificatesDisplayBehavior,
      displayBehaviorErrorFeedback: errorFields?.certificatesDisplayBehavior,
    },
  ];

  const enrollmentDateFields = [
    {
      labels: [
        intl.formatMessage(messages.scheduleEnrollmentStartDateLabel),
        intl.formatMessage(messages.scheduleEnrollmentStartTimeLabel),
      ],
      value: enrollmentStart,
      rowType: SCHEDULE_ROW_TYPES.datetime,
      helpText: intl.formatMessage(messages.scheduleEnrollmentStartDateHelpText),
      controlName: 'enrollmentStart',
      errorFeedback: errorFields?.enrollmentStart,
    },
    {
      labels: [
        intl.formatMessage(messages.scheduleEnrollmentEndDateLabel),
        intl.formatMessage(messages.scheduleEnrollmentEndTimeLabel),
      ],
      value: enrollmentEnd,
      rowType: SCHEDULE_ROW_TYPES.datetime,
      helpText: computedEnrollmentEndHelpText,
      readonly: !enrollmentEndEditable,
      controlName: 'enrollmentEnd',
      errorFeedback: errorFields?.enrollmentEnd,
    },
    {
      skip: !upgradeDeadline,
      labels: [
        intl.formatMessage(messages.scheduleUpgradeDeadlineDateLabel),
        intl.formatMessage(messages.scheduleUpgradeDeadlineTimeLabel),
      ],
      value: upgradeDeadline,
      rowType: SCHEDULE_ROW_TYPES.datetime,
      helpText: intl.formatMessage(
        messages.scheduleUpgradeDeadlineDateHelpText,
        { platformName },
      ),
      readonly: true,
      controlName: 'upgradeDeadline',
      errorFeedback: errorFields?.upgradeDeadline,
    },
  ];

  const renderDateFields = (fields) => fields
    .filter((field) => !field.skip)
    .map((fieldProps) => {
      if (fieldProps.rowType === SCHEDULE_ROW_TYPES.datetime) {
        const { skip, rowType, ...restProps } = fieldProps;
        return (
          <ScheduleRow
            {...restProps}
            key={fieldProps.controlName}
            onChange={onChange}
          />
        );
      }
      const { rowType, key, ...restProps } = fieldProps;
      return (
        <CertificateDisplayRow
          {...restProps}
          key={key}
          onChange={onChange}
        />
      );
    });

  return (
    <section className="section-container schedule-section" id="schedule">
      <SectionSubHeader
        title={intl.formatMessage(messages.scheduleTitle)}
        description={intl.formatMessage(messages.scheduleDescription)}
      />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 2rem' }}>
        <div>
          <h6 className="mt-3 mb-2">Course dates</h6>
          <ul className="schedule-date-list">
            {renderDateFields(courseDateFields)}
          </ul>
        </div>
        <div>
          <h6 className="mt-3 mb-2">Enrollment dates</h6>
          <ul className="schedule-date-list">
            {renderDateFields(enrollmentDateFields)}
          </ul>
        </div>
      </div>
      <div className="schedule-section-flags mt-3" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
        {SCHEDULE_FLAGS.map(({ key, label, description }) => (
          <Form.Checkbox
            key={key}
            checked={!!flagValues[key]}
            onChange={(e) => onChange(e.target.checked, key)}
            description={description || undefined}
          >
            {label}
          </Form.Checkbox>
        ))}
      </div>
    </section>
  );
};

ScheduleSection.defaultProps = {
  endDate: '',
  startDate: '',
  errorFields: {},
  enrollmentEnd: '',
  enrollmentStart: '',
  upgradeDeadline: '',
  certificateAvailableDate: '',
  courseLive: false,
  comingSoon: false,
  isSkillModule: false,
  isTrack: false,
  isPathway: false,
  skillBadging: false,
  hideFromCatalog: false,
};

ScheduleSection.propTypes = {
  endDate: PropTypes.string,
  startDate: PropTypes.string,
  errorFields: PropTypes.objectOf(PropTypes.string),
  platformName: PropTypes.string.isRequired,
  enrollmentEnd: PropTypes.string,
  enrollmentStart: PropTypes.string,
  upgradeDeadline: PropTypes.string,
  enrollmentEndEditable: PropTypes.bool.isRequired,
  certificateAvailableDate: PropTypes.string,
  certificatesDisplayBehavior: PropTypes.string.isRequired,
  canShowCertificateAvailableDateField: PropTypes.bool.isRequired,
  courseLive: PropTypes.bool,
  comingSoon: PropTypes.bool,
  isSkillModule: PropTypes.bool,
  isTrack: PropTypes.bool,
  isPathway: PropTypes.bool,
  skillBadging: PropTypes.bool,
  hideFromCatalog: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
};

export default ScheduleSection;
